/* ResearchStory.jsx — article view of a Daily Market Wrap edition,
 * per the story-page mockup. Reads the existing edition JSON; no new backend.
 */
import React, { useEffect, useState } from "react";
import "./research-home.css";
import NewsletterEdition from "../newsletter/NewsletterEdition";
import { useResearchLocation } from "../desk/researchNavigation";
import { go, fetchEditionByDate, fetchFeed, editionUrl, timeAgo, chgClass, formatPct } from "./homeData";

function stripCites(text) {
  return String(text || "").replace(/\[\d+\]/g, "");
}

function Paragraphs({ text }) {
  return <>{stripCites(text).split(/\n{2,}|\n/).filter((p) => p.trim()).map((p, i) => <p key={i}>{p.trim()}</p>)}</>;
}

function Tape({ edition }) {
  const tape = (edition?.tape || []).slice(0, 4);
  if (!tape.length) return null;
  return (
    <div className="rh-tape" aria-label="The tape">
      {tape.map((t, i) => (
        <div className="rh-tile" key={`${t.label}-${i}`}>
          <div className="rh-sub">{t.label}</div>
          <div className="rh-price" style={{ fontSize: 17 }}>{t.value || "—"}</div>
          <div className={`rh-chg ${chgClass(t.changePercent)}`}>{t.change || "—"}</div>
        </div>
      ))}
    </div>
  );
}

function CrossAssetTable({ edition }) {
  const rows = (edition?.tape || []).filter((t) => t.label && t.value);
  if (!rows.length) return null;
  return (
    <div className="rh-card" style={{ padding: 6, margin: "18px 0" }}>
      <table className="rh-table">
        <thead><tr><th>Asset</th><th>Close</th><th>Change</th><th>Read</th></tr></thead>
        <tbody>
          {rows.map((t, i) => (
            <tr key={`${t.label}-${i}`}>
              <td><strong>{t.label}</strong></td>
              <td className="rh-num">{t.value}</td>
              <td className={`rh-num ${chgClass(t.changePercent)}`}>{t.change || "—"}</td>
              <td style={{ color: "var(--rh-dim)" }}>{t.context || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SubscribeMini() {
  return (
    <div className="rh-subscribe">
      <h3>The AEON Brief</h3>
      <p>Read the evidence, scenarios and the next test.</p>
      <a href="/research/brief" onClick={go("/research/brief")} className="rh-btn rh-btn-orange" style={{ display: "block", textAlign: "center" }}>Read the brief</a>
    </div>
  );
}

export default function ResearchStory() {
  const path = useResearchLocation();
  const date = decodeURIComponent(path.replace("/research/story/", "").split("/")[0]);
  const [edition, setEdition] = useState(null);
  const [related, setRelated] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    let dead = false;
    setEdition(null); setErr("");
    (async () => {
      try {
        const [ed, items] = await Promise.all([
          fetchEditionByDate(date),
          fetchFeed(12).catch(() => []),
        ]);
        if (!dead) { setEdition(ed); setRelated(items); }
      } catch (e) { if (!dead) setErr("This story is unavailable right now."); }
    })();
    return () => { dead = true; };
  }, [date]);

  if (err) return <div className="rh"><div className="rh-wrap"><div className="rh-empty" style={{ margin: 24 }}>{err}</div></div></div>;
  if (!edition) return <div className="rh"><div className="rh-wrap"><div className="rh-empty" style={{ margin: 24 }}>Loading story…</div></div></div>;

  if (edition.newsletter?.version === 2) return <div className="rh"><div className="rh-wrap"><nav className="rh-crumb" aria-label="Breadcrumb"><a href="/research" onClick={go("/research")}>AEON Research</a> / {edition.date}</nav><NewsletterEdition edition={edition}/></div></div>;

  const content = edition.content || {};
  const sections = Object.entries(content.sections || {});
  const sources = edition.sources || [];
  const niceDate = (() => { try { return new Date(edition.date + "T12:00:00Z").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric", timeZone: "America/Chicago" }); } catch { return edition.date; } })();

  return (
    <div className="rh">
      <div className="rh-wrap">
        <nav className="rh-crumb" aria-label="Breadcrumb">
          <a href="/research" onClick={go("/research")}>Research</a> › Daily Market Wrap › {edition.date}
        </nav>
        <div className="rh-article">
          <article className="rh-article-body">
            <div className="rh-kicker" style={{ color: "#c2703d" }}>{content.kicker || `Daily Market Wrap · ${niceDate}`}</div>
            <h1>{content.headline || edition.headline}</h1>
            {content.dek && <p className="rh-dek">{stripCites(content.dek)}</p>}
            <div className="rh-article-meta">
              <span><strong style={{ color: "var(--rh-text)" }}>AEON Research</strong></span>
              {content.reading_time_minutes ? <span>{content.reading_time_minutes} min read</span> : null}
              <span className="rh-stance-pill">Published analysis</span>
            </div>
            <h3 className="rh-sect">The Tape</h3>
            <Tape edition={edition} />
            {sections.map(([key, s]) => (
              <section key={key}>
                <h2>{s.title}</h2>
                <Paragraphs text={s.body} />
                {key === "cross_asset" || key === "macro" ? <CrossAssetTable edition={edition} /> : null}
              </section>
            ))}
            {!sections.length && content.body && <Paragraphs text={content.body} />}
            <div className="rh-sources">
              <h3 className="rh-sect">Sources</h3>
              {sources.length ? (
                <ul>{sources.map((s, i) => <li key={i}><a href={s.url} target="_blank" rel="noopener noreferrer">{s.title || s.publisher || s.name || s.url}</a></li>)}</ul>
              ) : <p className="rh-note">Sources listed in the canonical edition. <a href={editionUrl(edition)} onClick={go(editionUrl(edition))}>Open it →</a></p>}
              <p className="rh-note" style={{ marginTop: 10 }}>Prices as reported {edition.date}. Not financial advice. Generated by AEON Research.</p>
            </div>
          </article>
          <aside className="rh-rail">
            <div className="rh-card">
              <h3>In this story</h3>
              <ul className="rh-feedlist">
                {(edition.tape || []).slice(0, 5).map((t, i) => (
                  <li key={i} style={{ display: "flex", justifyContent: "space-between" }}>
                    <span><strong>{t.label}</strong></span>
                    <span className={chgClass(t.changePercent)} style={{ fontFamily: "IBM Plex Mono, monospace" }}>{t.change || "—"}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rh-card">
              <h3>Outside coverage</h3>
              <ul className="rh-feedlist">
                {related.slice(0, 4).map((it) => (
                  <li key={it.id}><a href={it.url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--rh-text)", fontWeight: 600 }}>{it.title}</a><br /><span className="rh-note">{it.publisher || "External publisher"} · {timeAgo(it.publishedAt)}</span></li>
                ))}
                {!related.length && <li className="rh-note">No related stories yet.</li>}
              </ul>
            </div>
            <SubscribeMini />
          </aside>
        </div>
      </div>
    </div>
  );
}
